// Component History tab of the Components item record (H100).
//
// Legacy: mod_parts/includes/tabs/parts_tab_component_history.inc
//   Section 2 "Component History" (:235-290): a Based On filter and the grid
//   Type · Based On · Date · Aircraft REG/ENG No. · A/C@Unit · TSN · TSO · Comment,
//   from `component_history` (parts_ajax_handler.inc:2477-2490).
//   -> GET /v6/items/{id}/component-history[?based_on=]
// This is the install/removal log the earlier lanes looked for in parts_history
// (component-history-rewire-2026-09-19): legacy keeps it in component_history.
//
// 2026-10-14 (finishing the port): rows are added, edited and dropped by hand (:501-644,
// save_history / drop_history) through the slide-in ComponentHistorySheet ->
// POST / PATCH / DELETE /v6/items/{id}/component-history[/{row}]; the Date wears the tenant's
// format (BP-83). The grid is the detail-tab grid (BP-78/79): (+) and ACTIONS over ticks.
// NOT BUILT: drag-to-order (:470-499; legacy renumbers only the filtered rows, so priorities
// collide across Based On values), the Component History Report PDF (:287; its template lives in
// each install's data directory, not in the code), and Section 1 "Component Details" — the
// part-level parts_limitations grid (:10-45; 0 rows on g2 and Mustang, measured 2026-10-14).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import { DataGridPremium, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";

import { axios } from "../../../api";
import AlertDialog from "../../../components/AlertDialog";
import { ActionButton, CancelButton, CreateFab2 } from "../../../components/Buttons";
import { ContentGridBox } from "../../../components/DatagridProContnet";
import type { ActionMenuItem } from "../../../components/datagrid/ActionMenu";
import { commonDataGridPremiumSx } from "../../../utils/dataGridSx";
import CustomFilterPanelPremium from "../../../components/CustomFilterPanelPremium";
import { formatTenantDate, useTenantDateFormat } from "../../../utils/tenantDate";
import ComponentHistorySheet from "./ComponentHistorySheet";
import SubgridActions from "./SubgridActions";

export interface ComponentHistoryRow {
  id: number;
  history_type: string | null;
  type: string | null;
  based_on: string | null;
  record_date: string | null;
  ac_info: string | null;
  ac_unit: number | string | null;
  tsn: number | string | null;
  tso: number | string | null;
  comment: string | null;
}

export function historyColumns(datePattern: string | undefined): GridColDef<ComponentHistoryRow>[] {
  return [
    { field: "history_type", headerName: "Type", flex: 0.8, valueGetter: (_v, row) => row.history_type || row.type },
    { field: "based_on", headerName: "Based On", flex: 0.8 },
    {
      field: "record_date",
      headerName: "Date",
      flex: 0.8,
      valueFormatter: (value: string | null) => formatTenantDate(value, datePattern),
    },
    { field: "ac_info", headerName: "Aircraft REG/ENG No.", flex: 1 },
    { field: "ac_unit", headerName: "A/C@Unit", flex: 0.7 },
    { field: "tsn", headerName: "TSN", flex: 0.6 },
    { field: "tso", headerName: "TSO", flex: 0.6 },
    { field: "comment", headerName: "Comment", flex: 1.6 },
  ];
}

interface Props {
  itemId: number;
  /** No parts Edit grant, or the item is inactive: read-only (H100 record gate). */
  locked?: boolean;
}

const ComponentHistory: React.FC<Props> = ({ itemId, locked = true }) => {
  const { enqueueSnackbar } = useSnackbar();
  const datePattern = useTenantDateFormat();
  const [rows, setRows] = useState<ComponentHistoryRow[]>([]);
  const [basisOptions, setBasisOptions] = useState<string[]>([]);
  const [basedOn, setBasedOn] = useState("");
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [sheet, setSheet] = useState<{ open: boolean; row: ComponentHistoryRow | null }>({ open: false, row: null });
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    axios
      .get(`/v6/items/${itemId}/component-history`, {
        params: basedOn ? { based_on: basedOn } : {},
        cache: false,
      })
      .then((resp) => {
        if (!current) return;
        const data: ComponentHistoryRow[] = Array.isArray(resp.data) ? resp.data : [];
        setRows(data);
        if (!basedOn) {
          setBasisOptions(Array.from(new Set(data.map((r) => r.based_on).filter(Boolean) as string[])));
        }
      })
      .catch(() => current && enqueueSnackbar("Error fetching component history", { variant: "error" }))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [basedOn, enqueueSnackbar, itemId, reloadKey]);

  const reload = useCallback(() => {
    setSelection([]);
    setReloadKey((k) => k + 1);
  }, []);

  const columns = useMemo(() => historyColumns(datePattern), [datePattern]);
  const selectedRows = useMemo(() => rows.filter((r) => selection.includes(r.id)), [rows, selection]);
  const emptyText = !loading && rows.length === 0 ? "No History Found" : null;

  const actions: ActionMenuItem[] = locked
    ? []
    : [
        {
          name: "Edit History Row",
          requiresSelection: true,
          preventMultiSelect: true,
          multiSelectErrorMessage: "Select one row to edit.",
          onClick: () => setSheet({ open: true, row: selectedRows[0] ?? null }),
        },
        { name: "Delete History Row/s", requiresSelection: true, onClick: () => setConfirmDelete(true) },
      ];

  const deleteSelected = async () => {
    setConfirmDelete(false);
    const results = await Promise.allSettled(
      selectedRows.map((r) => axios.delete(`/v6/items/${itemId}/component-history/${r.id}`))
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed) enqueueSnackbar(`${failed} history row/s could not be deleted`, { variant: "error" });
    else enqueueSnackbar("History row/s deleted", { variant: "success" });
    reload();
  };

  return (
    <ContentGridBox>
      <Grid container sx={{ pb: 2 }}>
        <Grid item xs={12} sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          {!locked ? (
            <CreateFab2
              size="small"
              title="Add History Row"
              aria-label="Add History Row"
              onClick={() => setSheet({ open: true, row: null })}
            />
          ) : null}
          <SubgridActions popupId="component-history-actions" actions={actions} selectionCount={selection.length} />
          {basisOptions.length > 0 ? (
            <TextField
              select
              size="small"
              label="Based On"
              value={basedOn}
              onChange={(e) => setBasedOn(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">All</MenuItem>
              {basisOptions.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Grid>
      </Grid>
      {emptyText ? (
        <Typography variant="body2" sx={{ mb: 1 }} data-testid="component-history-empty">
          {emptyText}
        </Typography>
      ) : null}
      <DataGridPremium
        slots={{ filterPanel: CustomFilterPanelPremium }}
        autoHeight
        density="compact"
        rows={rows}
        columns={columns}
        loading={loading}
        checkboxSelection={!locked}
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        hideFooter={rows.length <= 15}
        sx={{ ...commonDataGridPremiumSx, borderLeft: "none", borderRight: "none" }}
        aria-label="component-history"
      />
      {/* mounted only while open: a closed sheet keeps no draft (BP-89) */}
      {sheet.open ? (
        <ComponentHistorySheet
          itemId={itemId}
          row={sheet.row}
          basedOnOptions={basisOptions}
          onClose={() => setSheet({ open: false, row: null })}
          onSaved={() => {
            setSheet({ open: false, row: null });
            reload();
          }}
        />
      ) : null}
      <AlertDialog
        open={confirmDelete}
        title="Delete history row/s?"
        description={`${selectedRows.length} row/s will be removed from this component's history.`}
        handleClose={() => setConfirmDelete(false)}
      >
        <CancelButton onClick={() => setConfirmDelete(false)} />
        <ActionButton onClick={deleteSelected}>Delete</ActionButton>
      </AlertDialog>
    </ContentGridBox>
  );
};

export default ComponentHistory;
